import React, { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../../utils/apiHelper';

import { formatPrice } from '../../utils/formatHelper';
import ActionDrawer from '../components/shared/ActionDrawer';
import { showSuccess, showError, showInfo } from '../../utils/toastHelper';

// Sabit kategoriler
const CATEGORIES = [
  'MAMA',
  'AKSESUAR',
  'OYUNCAK',
  'SAĞLIK',
  'BAKIM',
  'HİJYEN'
];

function ProductModal({ isOpen, onClose, product, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [mainImageLoading, setMainImageLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(new Set());
  const [deletingImages, setDeletingImages] = useState(new Set());
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    discountType: '',
    discountValue: '',
    discountStartDate: '',
    discountEndDate: '',
    images: [],
    filters: [],
    status: 'ACTIVE',
    stock: 0,
    shopifyId: '',
    imageMetadata: {}
  });
  const [images, setImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [mainImageUrl, setMainImageUrl] = useState(null);
  const [changeHistory, setChangeHistory] = useState([]);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        category: product.category || '',
        discountType: product.discountType || '',
        discountValue: product.discountValue || '',
        discountStartDate: product.discountStartDate ? product.discountStartDate : '',
        discountEndDate: product.discountEndDate ? product.discountEndDate : '',
        images: product.images || [],
        filters: product.filters || [],
        status: product.status || 'ACTIVE',
        stock: product.stock || 0,
        shopifyId: product.shopifyId || '',
        imageMetadata: product.imageMetadata || {}
      });

      if (product.images) {
        const previews = product.images.map(img => {
          // Fix broken URLs
          const hasValidUrl = img.url && !img.url.includes('undefined');
          return {
            url: hasValidUrl ? img.url : (img.originalUrl || img.url),
            isExisting: true,
            metadata: img.metadata || product.imageMetadata?.[img.url] || {}
          };
        });
        setPreviewImages(previews);
        setMainImageUrl(product.mainImage);
      }

      setChangeHistory(product.metadata?.changeHistory || []);
    } else {
      setFormData({
        name: '',
        description: '',
        price: '',
        category: '',
        discountType: '',
        discountValue: '',
        discountStartDate: '',
        discountEndDate: '',
        images: [],
        filters: [],
        status: 'ACTIVE',
        stock: 0,
        shopifyId: '',
        imageMetadata: {}
      });
      setImages([]);
      setPreviewImages([]);
      setMainImageUrl(null);
      setChangeHistory([]);
    }
    
    // Reset changes flag when modal opens
    if (isOpen) {
      setHasChanges(false);
    }
  }, [product, isOpen]);

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);

    // Yeni Ürün Modu
    if (!product) {
      const validFiles = files.filter(file => file.type.startsWith('image/'));

      if (validFiles.length !== files.length) {
        showError('Bazı dosyalar resim formatında değil ve atlandı');
      }

      // Temp URL oluştur
      const newPreviews = validFiles.map(file => ({
        url: URL.createObjectURL(file),
        isExisting: false,
        file: file,
        metadata: {
          originalName: file.name,
          mimeType: file.type,
          size: file.size
        }
      }));

      setPreviewImages(prev => [...prev, ...newPreviews]);
      setImages(prev => [...prev, ...validFiles]);

      e.target.value = '';
      return;
    }

    // Düzenleme Modu - Direkt Yükle
    for (const file of files) {
      const tempPreviewUrl = URL.createObjectURL(file);
      setPreviewImages(prev => [...prev, {
        url: tempPreviewUrl,
        isExisting: false,
        metadata: {
          originalName: file.name,
          mimeType: file.type,
          size: file.size
        }
      }]);

      try {
        setUploadingImages(prev => new Set([...prev, tempPreviewUrl]));

        const formDataPayload = new FormData();
        formDataPayload.append('image', file);

        const response = await apiRequest(`/products/${product.id}/images`, {
          method: 'POST',
          data: formDataPayload
        });

        if (response.success) {
          setPreviewImages(response.images);
          setMainImageUrl(response.mainImage);
          showSuccess('Görsel başarıyla yüklendi');
          if (response.warning) showError(response.warning);
          setHasChanges(true);
        } else {
          throw new Error(response.message || 'Görsel yüklenirken bir hata oluştu');
        }
      } catch (error) {
        setPreviewImages(prev => prev.filter(p => p.url !== tempPreviewUrl));
        showError(error.message || 'Görsel yüklenirken bir hata oluştu');
      } finally {
        setUploadingImages(prev => {
          const newSet = new Set(prev);
          newSet.delete(tempPreviewUrl);
          return newSet;
        });
        URL.revokeObjectURL(tempPreviewUrl);
      }
    }
    e.target.value = '';
  };

  const removeImage = async (index) => {
    if (!product) {
      setPreviewImages(prev => prev.filter((_, i) => i !== index));
      setImages(prev => prev.filter((_, i) => i !== index));
      return;
    }

    const imageToRemove = previewImages[index];
    if (!imageToRemove) return;

    try {
      setDeletingImages(prev => new Set([...prev, imageToRemove.url]));
      const response = await apiRequest(`/products/${product.id}/images/${encodeURIComponent(imageToRemove.url)}`, {
        method: 'DELETE'
      });

      if (response.success) {
        setPreviewImages(prev => prev.filter((_, i) => i !== index));
        showSuccess('Görsel başarıyla silindi');
        // Listede resim göstermediğimiz için silmede yenilemeye gerek yok diyebiliriz
        // ama yine de modal kapanınca senkronize olsun diye hasChanges yapıyoruz
        setHasChanges(true);
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      showError(error.message || 'Görsel silinirken hata oluştu');
    } finally {
      setDeletingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageToRemove.url);
        return newSet;
      });
    }
  };

  const handleSetMainImage = async (imageUrl) => {
    try {
      setMainImageLoading(true);
      const response = await apiRequest(`/products/${product?.id}/images/main`, {
        method: 'PUT',
        body: JSON.stringify({ mainImage: imageUrl })
      });

      if (response.success) {
        setFormData(prev => ({ ...prev, mainImage: imageUrl }));
        setMainImageUrl(imageUrl);
        showSuccess('Ana görsel güncellendi');
        setHasChanges(true);
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setMainImageLoading(false);
    }
  };

  const handleNewImages = useCallback((files) => {
    const validFiles = files.filter(file => file.type.startsWith('image/'));

    if (validFiles.length !== files.length) {
      showError('Bazı dosyalar resim formatında değil ve atlandı');
    }

    const newImages = [...images, ...validFiles];
    setImages(newImages);

    const newPreviews = validFiles.map(file => ({
      url: URL.createObjectURL(file),
      isExisting: false,
      metadata: {
        originalName: file.name,
        mimeType: file.type,
        size: file.size
      }
    }));

    setPreviewImages(prev => [...prev, ...newPreviews]);
  }, [images]);

  // Drag and Drop Handlers
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    // Edit modunda ise sadece uyarı göster, aslında implemente edilebilir ama drawer UX'inde karmaşık olabilir
    if (product) {
      showInfo("Düzenleme modunda sürükle-bırak için lütfen dosya seçiciyi kullanın");
    } else {
      handleNewImages(files);
    }
  }, [handleNewImages, product]);

  const handleDragStart = (index) => setDraggedIndex(index);
  const handleDragEnter = (index) => {
    if (draggedIndex === null) return;
    const newPreviewImages = [...previewImages];
    const draggedImage = newPreviewImages[draggedIndex];
    newPreviewImages.splice(draggedIndex, 1);
    newPreviewImages.splice(index, 0, draggedImage);

    // Create Mode Logic
    if (images.length > 0 && !product) {
      const newImages = [...images];
      if (draggedIndex < images.length && index < images.length) {
        const draggedFile = newImages[draggedIndex];
        newImages.splice(draggedIndex, 1);
        newImages.splice(index, 0, draggedFile);
        setImages(newImages);
      }
    }

    setPreviewImages(newPreviewImages);
    setDraggedIndex(index);
  };
  const handleDragEnd = () => setDraggedIndex(null);

  const calculateDiscountedPrice = (price, discountType, discountValue) => {
    const numericPrice = parseFloat(price);
    const numericDiscount = parseFloat(discountValue);

    if (discountType === 'PERCENTAGE') {
      return numericPrice - (numericPrice * (numericDiscount / 100));
    } else if (discountType === 'FIXED_AMOUNT') {
      return Math.max(0, numericPrice - numericDiscount);
    }
    return numericPrice;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (product) {
        // Edit Mode
        const changedFields = {
          name: formData.name,
          description: formData.description,
          category: formData.category,
          price: formData.price,
          stock: formData.stock,
          status: formData.status,
          filters: formData.filters || [],
          discountType: formData.discountType || null,
          discountValue: formData.discountValue || null,
          discountStartDate: formData.discountStartDate ? new Date(formData.discountStartDate).toISOString() : null,
          discountEndDate: formData.discountEndDate ? new Date(formData.discountEndDate).toISOString() : null,
        };

        const response = await apiRequest(`/products/${product.id}`, {
          method: 'PUT',
          body: JSON.stringify(changedFields)
        });

        if (response.success) {
          showSuccess('Ürün başarıyla güncellendi');
          if (onSuccess) onSuccess();
          onClose();
        } else {
          throw new Error(response.message);
        }
      } else {
        // Create Mode
        const formDataToSend = new FormData();
        Object.keys(formData).forEach(key => {
          if (key !== 'images') formDataToSend.append(key, formData[key]);
        });

        images.forEach(file => {
          if (file instanceof File) formDataToSend.append('images', file);
        });

        const response = await apiRequest('/products', {
          method: 'POST',
          data: formDataToSend
        });

        if (response.success) {
          showSuccess('Yeni ürün başarıyla oluşturuldu');
          if (onSuccess) onSuccess();
          onClose();
        } else {
          throw new Error(response.message);
        }
      }
    } catch (error) {
      showError(error.message || 'Hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const value = e.target.value.trim();
      if (value && !formData.filters.includes(value)) {
        setFormData(prev => ({
          ...prev,
          filters: [...prev.filters, value]
        }));
        e.target.value = '';
      }
    }
  };

  const removeFilter = (filterToRemove) => {
    setFormData(prev => ({
      ...prev,
      filters: prev.filters.filter(f => f !== filterToRemove)
    }));
  };

  const isMainImage = useCallback((preview) => {
    if (!preview || !mainImageUrl) return false;
    if (preview.url === mainImageUrl) return true;

    // Check metadata cross-reference
    if (product && product.imageMetadata) {
      // Get metadata for the current mainImage (which might be the S3 URL)
      const mainMeta = product.imageMetadata[mainImageUrl];
      if (mainMeta && mainMeta.originalUrl) {
        // Check if preview URL matches the originalUrl of the main image
        if (preview.url === mainMeta.originalUrl) return true;
        // Check if preview's metadata originalUrl matches
        if (preview.metadata && preview.metadata.originalUrl === mainMeta.originalUrl) return true;
      }
    }
    return false;
  }, [mainImageUrl, product]);

  const handleClose = useCallback(() => {
    if (hasChanges && onSuccess) {
      onSuccess();
    }
    onClose();
  }, [hasChanges, onSuccess, onClose]);

  const footer = (
    <div className="d-flex justify-content-end gap-2 w-100">
      <button type="button" className="btn btn-light border" onClick={handleClose}>
        İptal
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Kaydediliyor...</> : 'Kaydet'}
      </button>
    </div>
  );

  return (
    <ActionDrawer
      isOpen={isOpen}
      onClose={handleClose}
      title={product ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
      subtitle={product ? `${product.name} ürününü düzenliyorsunuz` : 'Yeni bir ürün oluşturun'}
      width="800px"
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="row g-4 product-form-content">
        {/* Görseller Bölümü */}
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label fw-bold mb-0">Ürün Görselleri</label>
            <small className="text-muted"><i className="fas fa-star text-warning"></i> İkonuna tıklayarak kapak resmini seçebilirsiniz.</small>
          </div>
          <div className="card bg-light border-dashed">
            <div className="card-body text-center p-4">
              <input
                type="file"
                id="drawerProductImages"
                className="d-none"
                onChange={handleImageChange}
                multiple
                accept="image/*"
              />
              <label htmlFor="drawerProductImages" className="cursor-pointer d-block">
                <i className="fas fa-cloud-upload-alt fa-3x text-secondary mb-3"></i>
                <h6 className="text-dark">Görselleri Yükle</h6>
                <small className="text-muted">Tıklayarak resim seçebilirsiniz (JPG, PNG)</small>
              </label>
            </div>
          </div>

          {/* Preview Grid */}
          {previewImages.length > 0 && (
            <div className="d-flex flex-wrap gap-3 mt-3">
              {previewImages.map((preview, index) => {
                const isActive = isMainImage(preview);
                return (
                  <div
                    key={index}
                    className="position-relative border rounded overflow-hidden image-preview-item"
                    style={{ width: '100px', height: '100px' }}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragEnter={() => handleDragEnter(index)}
                    onDragEnd={handleDragEnd}
                  >
                    <img src={preview.url} alt="Preview" className="w-100 h-100 object-fit-cover" />

                    {/* Loading Overlay */}
                    {(uploadingImages.has(preview.url) || deletingImages.has(preview.url)) && (
                      <div className="loading-overlay">
                        <span className="spinner-border text-white"></span>
                      </div>
                    )}

                    <button
                      type="button"
                      className="btn btn-danger btn-sm p-0 position-absolute top-0 end-0 rounded-0"
                      style={{ width: '24px', height: '24px' }}
                      onClick={() => removeImage(index)}
                    >
                      <i className="fas fa-times" style={{ fontSize: '12px' }}></i>
                    </button>

                    <button
                      type="button"
                      className={`set-main-image ${isActive ? 'active' : ''}`}
                      onClick={(e) => { e.stopPropagation(); handleSetMainImage(preview.url); }}
                      disabled={mainImageLoading || !product}
                      title={isActive ? "Şu anki ana görsel" : "Ana görsel olarak ayarla"}
                      aria-label={isActive ? "Şu anki ana görsel" : "Ana görsel yap"}
                    >
                      <i className="fas fa-star" style={{ fontSize: '14px' }}></i>
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="col-md-8">
          <div className="mb-3">
            <label className="form-label">Ürün Adı*</label>
            <input
              type="text"
              className="form-control"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Açıklama</label>
            <textarea
              className="form-control"
              rows="4"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            ></textarea>
          </div>

          <div className="mb-3">
            <label className="form-label">Etiketler (Filtreler)</label>
            <div className="filter-tags-container">
              {formData.filters.length > 0 && (
                <div className="filter-tags d-flex flex-wrap gap-1 mb-2">
                  {formData.filters.map((tag, idx) => (
                    <span key={idx} className="filter-tag">
                      {tag}
                      <button type="button" className="remove-tag" onClick={() => removeFilter(tag)}>
                        <i className="fas fa-times"></i>
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <input
                type="text"
                className="form-control border-0 p-0 shadow-none"
                placeholder="+ Enter veya virgül ile ekleyebilirsiniz"
                onKeyDown={handleFilterKeyDown}
              />
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card bg-light border-0">
            <div className="card-body">
              <h6 className="card-title fw-bold mb-3">Fiyat & Stok</h6>
              <div className="mb-3">
                <label className="form-label small">Fiyat (TL)*</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="mb-3">
                <label className="form-label small">Stok*</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  required
                  min="0"
                />
              </div>
              <div className="mb-3">
                <label className="form-label small">Kategori</label>
                <input
                  type="text"
                  className="form-control"
                  list="categoryOptions"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                />
                <datalist id="categoryOptions">
                  {CATEGORIES.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div className="mb-3">
                <label className="form-label small">Durum</label>
                <select
                  className="form-select"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="ACTIVE">Aktif</option>
                  <option value="PASSIVE">Pasif</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* İndirim Bölümü */}
        <div className="col-12">
          <div className="border-top pt-3">
            <h6 className="fw-bold mb-3">İndirim Ayarları</h6>
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label small">İndirim Tipi</label>
                <select
                  className="form-select form-select-sm"
                  value={formData.discountType}
                  onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                >
                  <option value="">Yok</option>
                  <option value="PERCENTAGE">Yüzde (%)</option>
                  <option value="FIXED_AMOUNT">Tutar (TL)</option>
                </select>
              </div>
              {formData.discountType && (
                <>
                  <div className="col-md-3">
                    <label className="form-label small">Değer</label>
                    <input
                      type="number"
                      className="form-control form-select-sm"
                      value={formData.discountValue}
                      onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                    />
                    <small className="text-success d-block mt-1" style={{ fontSize: '0.75rem' }}>
                      İndirimli: {formatPrice(calculateDiscountedPrice(formData.price, formData.discountType, formData.discountValue))}
                    </small>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small">Başlangıç</label>
                    <input
                      type="datetime-local"
                      className="form-control form-select-sm"
                      value={formData.discountStartDate}
                      onChange={(e) => setFormData({ ...formData, discountStartDate: e.target.value })}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small">Bitiş</label>
                    <input
                      type="datetime-local"
                      className="form-control form-select-sm"
                      value={formData.discountEndDate}
                      onChange={(e) => setFormData({ ...formData, discountEndDate: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Değişiklik Geçmişi (Sadece düzenlemede) */}
        {product && changeHistory.length > 0 && (
          <div className="col-12 mt-4">
            <h6 className="fw-bold mb-3">Değişiklik Geçmişi</h6>
            <div className="change-history p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {changeHistory.map((item, idx) => (
                <div key={idx} className="change-item mb-2 p-2 border rounded bg-white small">
                  <div className="d-flex justify-content-between text-muted">
                    <span>{new Date(item.timestamp).toLocaleString('tr-TR')}</span>
                    <span>{item.userName}</span>
                  </div>
                  <div className="mt-1">
                    {Object.keys(item.changes).map(field => (
                      <div key={field}>
                        <strong>{field}:</strong> {JSON.stringify(item.changes[field].from)} → {JSON.stringify(item.changes[field].to)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </form>

      <style>{`
          .border-dashed { border-style: dashed !important; }
          .loading-overlay { 
             background: rgba(0,0,0,0.5); 
             position: absolute; 
             top:0; left:0; right:0; bottom:0; 
             display: flex; align-items: center; justify-content: center;
             z-index: 5; 
          }
          .set-main-image {
             position: absolute; bottom: 5px; left: 5px;
             border: none; background: rgba(255,255,255,0.8);
             border-radius: 50%; width: 24px; height: 24px;
             display: flex; align-items: center; justify-content: center;
             cursor: pointer; color: #ccc;
          }
          .set-main-image.active { color: orange; background: white; }
          .set-main-image:hover { background: white; transform: scale(1.1); }
          
          .image-preview-item:hover .btn-danger { display: block; }
          
          .filter-tags-container {
             border: 1px solid #dee2e6;
             border-radius: 0.375rem;
             padding: 0.5rem;
          }
          .filter-tag {
             background: var(--kito-orange, #fd7e14);
             color: white;
             padding: 2px 8px;
             border-radius: 12px;
             font-size: 0.8em;
             margin-right: 5px;
             display: inline-flex; align-items: center;
          }
          .remove-tag {
             background: none; border: none; color: white; margin-left: 5px; padding: 0; cursor: pointer;
          }
      `}</style>
    </ActionDrawer>
  );
}

export default ProductModal;